import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  FileText, RefreshCw, Search, Loader2, Send, DollarSign, CheckCircle2,
  Download, ScrollText, ChevronRight, Receipt, X, FileMinus, Link as LinkIcon,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/finance`;

const STATUS_STYLE = {
  draft:            'bg-slate-100 text-slate-700 border-slate-200',
  finance_review:   'bg-amber-100 text-amber-800 border-amber-200',
  approved:         'bg-blue-100 text-blue-800 border-blue-200',
  issued:           'bg-indigo-100 text-indigo-800 border-indigo-200',
  sent:             'bg-indigo-100 text-indigo-800 border-indigo-200',
  viewed:           'bg-blue-100 text-blue-800 border-blue-200',
  payment_pending:  'bg-amber-100 text-amber-800 border-amber-200',
  partially_paid:   'bg-orange-100 text-orange-800 border-orange-200',
  paid:             'bg-emerald-100 text-emerald-800 border-emerald-200',
  closed:           'bg-slate-100 text-slate-700 border-slate-200',
  void:             'bg-rose-100 text-rose-800 border-rose-200',
  overdue:          'bg-rose-100 text-rose-800 border-rose-200',
};

const STATUS_FILTERS = [
  { v: '', l: 'All' },
  { v: 'draft', l: 'Draft' },
  { v: 'approved', l: 'Approved' },
  { v: 'sent', l: 'Sent' },
  { v: 'partially_paid', l: 'Partial' },
  { v: 'paid', l: 'Paid' },
];

const PAYMENT_METHODS = [
  { v: 'ach', l: 'Direct Deposit — ACH' },
  { v: 'eft', l: 'Direct Deposit — EFT' },
];

const fmtMoney = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (v) => v ? new Date(v).toLocaleDateString() : '—';

const GovInvoices = () => {
  const { user: me } = useAuth();
  const isFinance = ['finance_officer', 'admin', 'super_admin', 'uske_finance_officer'].includes(me?.role);
  const canRecordPayment = ['uske_finance_officer', 'admin', 'super_admin'].includes(me?.role) && !me?.view_only; // USKE staff only
  const canNotifyPayment = ['finance_officer', 'agency_admin'].includes(me?.role) && !me?.view_only; // Agency-side FO/Admin
  const canIssueCreditNote = ['uske_finance_officer', 'admin', 'super_admin'].includes(me?.role) && !me?.view_only; // USKE FO only (Agency FOs excluded)
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [detail, setDetail] = useState(null);          // invoice-detail modal
  const [payments, setPayments] = useState([]);
  const [audit, setAudit] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyForm, setNotifyForm] = useState({ amount: '', method: 'ach', reference_number: '', bank: '', payment_date: '', notes: '' });
  const [notifySubmitting, setNotifySubmitting] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const [linkActivity, setLinkActivity] = useState(null);
  const [payForm, setPayForm] = useState({ amount: '', method: 'ach', reference_number: '', bank: '', payment_date: '', notes: '' });
  const [paySubmitting, setPaySubmitting] = useState(false);
  const [payPrefillMeta, setPayPrefillMeta] = useState(null); // { notified_by_name, notified_at } — surfaced above the form
  const [issueOpen, setIssueOpen] = useState(false);
  const [issueTo, setIssueTo] = useState('');
  const [crOpen, setCrOpen] = useState(false);
  const [crForm, setCrForm] = useState({ reason_code: 'adjustment', reason: '', total: '', apply_to_invoice: true });
  const [crSubmitting, setCrSubmitting] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (status) params.set('status', status);
      const res = await axios.get(`${API}/invoices?${params.toString()}`);
      setInvoices(res.data || []);
    } catch (e) {
      toast({ title: 'Load failed', description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [q, status]);
  useEffect(() => { fetchList(); }, [fetchList]);

  const kpi = useMemo(() => {
    const paid = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + Number(i.amount_paid || 0), 0);
    const outstanding = invoices.reduce((s, i) => s + Number(i.balance_due || 0), 0);
    const draft = invoices.filter(i => i.status === 'draft').length;
    const overdue = invoices.filter(i => i.balance_due > 0 && i.due_date && new Date(i.due_date) < new Date()).length;
    return { paid, outstanding, draft, overdue };
  }, [invoices]);

  const openDetail = async (inv) => {
    setDetail(inv); setPayments([]); setAudit([]); setReceipts([]); setLinkActivity(null);
    try {
      const [p, a, r, la] = await Promise.all([
        axios.get(`${API}/invoices/${inv.id}/payments`).catch(() => ({ data: [] })),
        axios.get(`${API}/invoices/${inv.id}/audit`).catch(() => ({ data: [] })),
        axios.get(`${API}/invoices/${inv.id}/receipts`).catch(() => ({ data: [] })),
        axios.get(`${API}/invoices/${inv.id}/link-activity`).catch(() => ({ data: null })),
      ]);
      setPayments(p.data || []);
      setAudit(a.data || []);
      setReceipts(r.data || []);
      setLinkActivity(la.data || null);
    } catch (e) {
      // detail modal — payments/audit best-effort
    }
  };
  const closeDetail = () => setDetail(null);

  const downloadReceipt = async (receiptNo) => {
    try {
      const res = await axios.get(`${API}/receipts/${receiptNo}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `Receipt-${receiptNo}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast({ title: 'Receipt downloaded', description: receiptNo });
    } catch (e) {
      toast({ title: 'Download failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const copySecureLink = async (inv) => {
    try {
      const r = await axios.post(`${API}/invoices/${inv.id}/payment-link`);
      await navigator.clipboard.writeText(r.data.url);
      toast({
        title: 'Secure link copied',
        description: `Expires ${new Date(r.data.expires_at).toLocaleDateString()} · Invoice ${r.data.invoice_no}`,
      });
      // Refresh activity feed if the detail modal is open for this invoice
      if (detail?.id === inv.id) {
        try {
          const la = await axios.get(`${API}/invoices/${inv.id}/link-activity`);
          setLinkActivity(la.data || null);
        } catch (_) { /* ignore */ }
      }
    } catch (e) {
      toast({ title: 'Link creation failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const openRecordPayment = async (inv) => {
    // Prefill from the most recent Agency payment notification (if any) so the
    // USKE FO doesn't have to re-key the same figures.
    setPayPrefillMeta(null);
    let base = { amount: inv.balance_due, method: 'ach', reference_number: '', bank: '',
                  payment_date: new Date().toISOString().slice(0, 10), notes: '' };
    try {
      const r = await axios.get(`${API}/invoices/${inv.id}/latest-notification`);
      if (r.data) {
        const n = r.data;
        base = {
          amount: (n.amount ?? inv.balance_due) || '',
          method: n.method || 'ach',
          reference_number: n.reference_number || '',
          bank: n.bank || '',
          payment_date: n.payment_date ? new Date(n.payment_date).toISOString().slice(0, 10)
                                        : new Date().toISOString().slice(0, 10),
          notes: n.notes || '',
        };
        setPayPrefillMeta({
          notified_by: n.notified_by_name || n.notified_by_email,
          notified_at: n.notified_at,
        });
      }
    } catch (e) { /* best-effort — fall through with defaults */ }
    setPayForm(base);
    setPayOpen(true);
  };

  const submitNotifyPayment = async () => {
    if (!detail) return;
    setNotifySubmitting(true);
    try {
      const body = {
        amount: notifyForm.amount ? parseFloat(notifyForm.amount) : null,
        method: notifyForm.method,
        reference_number: notifyForm.reference_number || null,
        bank: notifyForm.bank || null,
        notes: notifyForm.notes || null,
        payment_date: notifyForm.payment_date ? new Date(notifyForm.payment_date).toISOString() : null,
      };
      const r = await axios.post(`${API}/invoices/${detail.id}/notify-payment`, body);
      toast({ title: 'USKE notified', description: r.data.message });
      setNotifyOpen(false);
      setNotifyForm({ amount: '', method: 'ach', reference_number: '', bank: '', payment_date: '', notes: '' });
      await fetchList();
      setDetail(await refreshDetail());
    } catch (e) {
      toast({ title: 'Notify failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setNotifySubmitting(false); }
  };

  const doApprove = async () => {
    try { await axios.post(`${API}/invoices/${detail.id}/approve`, {}); toast({ title: 'Invoice approved' }); await fetchList(); openDetail(await refreshDetail()); }
    catch (e) { toast({ title: 'Approve failed', description: e.response?.data?.detail || '', variant: 'destructive' }); }
  };
  const refreshDetail = async () => (await axios.get(`${API}/invoices/${detail.id}`)).data;

  const doIssue = async () => {
    try {
      const res = await axios.post(`${API}/invoices/${detail.id}/issue`, { recipient_email: issueTo || undefined });
      toast({ title: 'Invoice sent', description: `to ${res.data.to}` });
      setIssueOpen(false); setIssueTo('');
      await fetchList(); setDetail(await refreshDetail());
    } catch (e) { toast({ title: 'Send failed', description: e.response?.data?.detail || '', variant: 'destructive' }); }
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    setPaySubmitting(true);
    try {
      const res = await axios.post(`${API}/invoices/${detail.id}/payments`, {
        amount: Number(payForm.amount),
        method: payForm.method,
        reference_number: payForm.reference_number || undefined,
        bank: payForm.bank || undefined,
        payment_date: payForm.payment_date ? new Date(payForm.payment_date).toISOString() : undefined,
        notes: payForm.notes || undefined,
      });
      if (res.data.receipt_no) {
        toast({ title: 'Payment recorded & receipt issued', description: res.data.receipt_no });
      } else {
        toast({ title: 'Payment recorded', description: `Balance: ${fmtMoney(res.data.balance_due)}` });
      }
      setPayOpen(false);
      setPayForm({ amount: '', method: 'ach', reference_number: '', bank: '', payment_date: '', notes: '' });
      setPayPrefillMeta(null);
      await fetchList(); setDetail(await refreshDetail());
      // Refresh payments + audit
      const [p, a] = await Promise.all([axios.get(`${API}/invoices/${detail.id}/payments`), axios.get(`${API}/invoices/${detail.id}/audit`)]);
      setPayments(p.data || []); setAudit(a.data || []);
    } catch (err) { toast({ title: 'Payment failed', description: err.response?.data?.detail || '', variant: 'destructive' }); }
    finally { setPaySubmitting(false); }
  };

  const submitCreditNote = async (e) => {
    e.preventDefault();
    setCrSubmitting(true);
    try {
      const res = await axios.post(`${API}/credit-notes`, {
        invoice_id: detail.id,
        reason: crForm.reason,
        reason_code: crForm.reason_code,
        total: crForm.total ? Number(crForm.total) : undefined,
        apply_to_invoice: !!crForm.apply_to_invoice,
      });
      toast({ title: 'Credit note issued', description: res.data.credit_note_no });
      setCrOpen(false);
      setCrForm({ reason_code: 'adjustment', reason: '', total: '', apply_to_invoice: true });
      await fetchList(); setDetail(await refreshDetail());
      const [p, a] = await Promise.all([axios.get(`${API}/invoices/${detail.id}/payments`), axios.get(`${API}/invoices/${detail.id}/audit`)]);
      setPayments(p.data || []); setAudit(a.data || []);
    } catch (err) { toast({ title: 'Credit note failed', description: err.response?.data?.detail || '', variant: 'destructive' }); }
    finally { setCrSubmitting(false); }
  };

  const downloadPdf = (inv) => {
    const url = `${API}/invoices/${inv.id}/pdf`;
    window.open(url, '_blank');
  };

  return (
    <div className="space-y-4" data-testid="gov-invoices-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2">
            <FileText className="h-7 w-7 text-blue-700" /> Invoices
          </h1>
          <p className="text-sm text-slate-500">{isFinance ? 'Approve, issue, and collect payments.' : 'View and download your organisation\'s invoices.'}</p>
        </div>
        <Button variant="outline" onClick={fetchList} data-testid="invoices-refresh"><RefreshCw className="h-4 w-4" /></Button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="p-3 rounded-lg border-2 border-emerald-200 bg-emerald-50"><div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Paid</div><div className="text-xl font-bold text-emerald-900">{fmtMoney(kpi.paid)}</div></div>
        <div className="p-3 rounded-lg border-2 border-blue-200 bg-blue-50"><div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">Outstanding</div><div className="text-xl font-bold text-blue-900">{fmtMoney(kpi.outstanding)}</div></div>
        <div className="p-3 rounded-lg border-2 border-amber-200 bg-amber-50"><div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Drafts</div><div className="text-xl font-bold text-amber-900">{kpi.draft}</div></div>
        <div className="p-3 rounded-lg border-2 border-rose-200 bg-rose-50"><div className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold">Overdue</div><div className="text-xl font-bold text-rose-900">{kpi.overdue}</div></div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search INV#, SO#, PO#, agency, receipt…" className="pl-7" data-testid="invoice-search" />
        </div>
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList>
            {STATUS_FILTERS.map(s => <TabsTrigger key={s.v} value={s.v} data-testid={`filter-${s.v || 'all'}`}>{s.l}</TabsTrigger>)}
          </TabsList>
        </Tabs>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-8 w-8 animate-spin text-blue-700" /></div>
      ) : invoices.length === 0 ? (
        <Card className="border-2 border-dashed"><CardContent className="p-10 text-center text-slate-400">No invoices match your filter.</CardContent></Card>
      ) : (
        <Card className="border-2">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <tr>
                    <th className="text-left p-3">Invoice #</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Agency</th>
                    <th className="text-left p-3">SO / PO</th>
                    <th className="text-right p-3">Total</th>
                    <th className="text-right p-3">Balance Due</th>
                    <th className="text-left p-3">Due</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(inv => (
                    <tr key={inv.id} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(inv)} data-testid={`inv-row-${inv.id}`}>
                      <td className="p-3">
                        <code className="text-[11px] bg-slate-100 px-1.5 py-0.5 rounded border font-mono">{inv.invoice_no}</code>
                        {inv.receipt_no && <div className="text-[10px] text-emerald-700 mt-0.5">✓ Receipt {inv.receipt_no}</div>}
                      </td>
                      <td className="p-3"><span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded border ${STATUS_STYLE[inv.status] || STATUS_STYLE.draft}`}>{inv.status.replace('_', ' ')}</span></td>
                      <td className="p-3 text-xs">{inv.agency_name || '—'}</td>
                      <td className="p-3 text-xs text-slate-500">
                        {inv.sales_order_no && <div>SO: <span className="font-mono">{inv.sales_order_no}</span></div>}
                        {inv.customer_po && <div>PO: <span className="font-mono">{inv.customer_po}</span></div>}
                      </td>
                      <td className="p-3 text-right font-mono">{fmtMoney(inv.total)}</td>
                      <td className="p-3 text-right font-mono">
                        <span className={inv.balance_due > 0 ? 'text-rose-700 font-semibold' : 'text-emerald-700'}>{fmtMoney(inv.balance_due)}</span>
                      </td>
                      <td className="p-3 text-xs text-slate-500">{fmtDate(inv.due_date)}</td>
                      <td className="p-3 text-right">
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); downloadPdf(inv); }} title="Download PDF" data-testid={`pdf-${inv.id}`}><Download className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); copySecureLink(inv); }} title="Copy 7-day secure payment link" data-testid={`share-link-${inv.id}`}><LinkIcon className="h-3.5 w-3.5" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={closeDetail}>
          <Card className="w-full max-w-3xl bg-white max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="invoice-detail-dialog">
            <CardHeader className="border-b pb-3 flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4 text-blue-700" />
                  <code className="text-sm">{detail.invoice_no}</code>
                  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded border ${STATUS_STYLE[detail.status]}`}>{detail.status.replace('_', ' ')}</span>
                </CardTitle>
                <p className="text-xs text-slate-500">{detail.agency_name}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => downloadPdf(detail)} data-testid="detail-pdf-btn"><Download className="h-3.5 w-3.5 mr-1" /> PDF</Button>
                <Button size="sm" variant="outline" onClick={() => copySecureLink(detail)} data-testid="detail-share-link-btn" title="Copy 7-day signed payment-instructions link"><LinkIcon className="h-3.5 w-3.5 mr-1" /> Share Payment Link</Button>
                {isFinance && detail.status === 'draft' && (
                  <Button size="sm" className="bg-blue-700 hover:bg-blue-800 text-white" onClick={doApprove} data-testid="detail-approve-btn"><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve</Button>
                )}
                {isFinance && detail.status !== 'draft' && detail.status !== 'paid' && (
                  <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => { setIssueTo(detail.bill_to?.email || ''); setIssueOpen(true); }} data-testid="detail-issue-btn"><Send className="h-3.5 w-3.5 mr-1" /> Send</Button>
                )}
                {canRecordPayment && detail.balance_due > 0 && detail.status !== 'draft' && (
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => openRecordPayment(detail)} data-testid="detail-record-payment-btn"><DollarSign className="h-3.5 w-3.5 mr-1" /> Record payment</Button>
                )}
                {canNotifyPayment && detail.balance_due > 0 && !['draft', 'paid', 'cancelled', 'void'].includes(detail.status) && (
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { setNotifyForm({ ...notifyForm, amount: detail.balance_due }); setNotifyOpen(true); }} data-testid="detail-notify-payment-btn"><Send className="h-3.5 w-3.5 mr-1" /> Notify USKE of Payment</Button>
                )}
                {receipts.length > 0 && (
                  <Button size="sm" variant="outline" onClick={() => downloadReceipt(receipts[0].receipt_no)} data-testid="detail-download-receipt-btn" title={`Latest receipt ${receipts[0].receipt_no}`}>
                    <FileText className="h-3.5 w-3.5 mr-1" /> Download Receipt
                  </Button>
                )}
                {canIssueCreditNote && detail.status !== 'draft' && (
                  <Button size="sm" variant="outline" className="border-amber-400 text-amber-800" onClick={() => { setCrForm({ ...crForm, total: detail.balance_due || detail.total }); setCrOpen(true); }} data-testid="detail-credit-note-btn"><FileMinus className="h-3.5 w-3.5 mr-1" /> Credit Note</Button>
                )}
                {!isFinance && detail.balance_due > 0 && !detail.disputed && (
                  <Button size="sm" variant="outline" className="border-rose-300 text-rose-700" onClick={async () => {
                    const reason = window.prompt('Dispute reason (please explain briefly):');
                    if (!reason || !reason.trim()) return;
                    try {
                      await axios.post(`${API}/invoices/${detail.id}/dispute`, { reason });
                      toast({ title: 'Invoice disputed', description: 'USKE Finance has been notified.' });
                      await fetchList(); setDetail(await refreshDetail());
                    } catch (e) { toast({ title: 'Dispute failed', description: e.response?.data?.detail || '', variant: 'destructive' }); }
                  }} data-testid="detail-dispute-btn">Dispute</Button>
                )}
                <Button size="sm" variant="ghost" onClick={closeDetail}><X className="h-4 w-4" /></Button>
              </div>
            </CardHeader>
            <CardContent className="overflow-y-auto space-y-4 p-4">
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div className="bg-slate-50 border rounded p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Bill to</div>
                  <div className="font-medium">{detail.bill_to?.name || '—'}</div>
                  <div className="text-slate-600">{detail.bill_to?.email}</div>
                  <div className="text-slate-500">{detail.bill_to?.address}{detail.bill_to?.city && `, ${detail.bill_to.city}`} {detail.bill_to?.state} {detail.bill_to?.zip}</div>
                </div>
                <div className="bg-slate-50 border rounded p-3 space-y-1">
                  <div className="flex justify-between"><span className="text-slate-500">Issue date</span><span>{fmtDate(detail.issue_date)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Due date</span><span>{fmtDate(detail.due_date)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Terms</span><span>{detail.payment_terms}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">SO / PO</span><span className="font-mono text-[11px]">{detail.sales_order_no || '—'} / {detail.customer_po || '—'}</span></div>
                </div>
              </div>

              {(detail.cost_center || detail.delivery_location || detail.department) && (
                <div className="grid grid-cols-2 gap-4 text-xs">
                  {detail.department && (
                    <div className="bg-purple-50 border border-purple-200 rounded p-3 col-span-2" data-testid="invoice-department">
                      <div className="text-[10px] uppercase tracking-wider text-purple-700 font-semibold mb-1">Department</div>
                      <div className="font-medium">
                        {detail.department.name}
                        {detail.department.code && <code className="ml-2 text-[10px] bg-white px-1.5 py-0.5 rounded border font-mono">{detail.department.code}</code>}
                      </div>
                    </div>
                  )}
                  {detail.cost_center && (
                    <div className="bg-indigo-50 border border-indigo-200 rounded p-3" data-testid="invoice-cost-center">
                      <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold mb-1">Cost Center</div>
                      <div className="font-medium">
                        <code className="text-[11px] bg-white px-1.5 py-0.5 rounded border font-mono">{detail.cost_center.code}</code> {detail.cost_center.name}
                      </div>
                      {detail.cost_center.gl_account && <div className="text-slate-500 mt-0.5">GL {detail.cost_center.gl_account}</div>}
                    </div>
                  )}
                  {detail.delivery_location && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded p-3" data-testid="invoice-delivery-location">
                      <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">Delivery Location</div>
                      <div className="font-medium">{detail.delivery_location.name}</div>
                      <div className="text-slate-600">
                        {detail.delivery_location.address_line1}
                        {detail.delivery_location.address_line2 ? `, ${detail.delivery_location.address_line2}` : ''}
                      </div>
                      <div className="text-slate-500">{detail.delivery_location.city}, {detail.delivery_location.state} {detail.delivery_location.zip}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Lines */}
              <div className="border rounded overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 text-slate-500 uppercase text-[10px]">
                    <tr><th className="text-left p-2">Item</th><th className="text-left p-2">SKU</th><th className="text-right p-2">Qty</th><th className="text-right p-2">Unit $</th><th className="text-right p-2">Amount</th></tr>
                  </thead>
                  <tbody>
                    {(detail.lines || []).map((l, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-2">{l.description}</td>
                        <td className="p-2 font-mono text-slate-500">{l.sku || '—'}</td>
                        <td className="p-2 text-right">{l.quantity}</td>
                        <td className="p-2 text-right">{fmtMoney(l.unit_price)}</td>
                        <td className="p-2 text-right font-mono">{fmtMoney(l.quantity * l.unit_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-72 space-y-1 text-sm">
                  <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">{fmtMoney(detail.subtotal)}</span></div>
                  {detail.discount > 0 && <div className="flex justify-between text-rose-700"><span>Discount</span><span className="font-mono">({fmtMoney(detail.discount)})</span></div>}
                  {detail.shipping > 0 && <div className="flex justify-between"><span>Shipping</span><span className="font-mono">{fmtMoney(detail.shipping)}</span></div>}
                  <div className="flex justify-between"><span>Tax {detail.tax > 0 ? `(${detail.tax_rate}%)` : '(exempt)'}</span><span className="font-mono">{fmtMoney(detail.tax)}</span></div>
                  <div className="flex justify-between font-bold border-t pt-1"><span>Grand total</span><span className="font-mono">{fmtMoney(detail.total)}</span></div>
                  <div className="flex justify-between text-emerald-700"><span>Paid</span><span className="font-mono">{fmtMoney(detail.amount_paid)}</span></div>
                  <div className={`flex justify-between font-bold ${detail.balance_due > 0 ? 'text-rose-700' : 'text-emerald-700'}`}><span>Balance due</span><span className="font-mono">{fmtMoney(detail.balance_due)}</span></div>
                </div>
              </div>

              {/* Payments */}
              {payments.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Payments ({payments.length})</div>
                  <table className="w-full text-xs border rounded">
                    <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]"><tr><th className="text-left p-2">Date</th><th className="text-left p-2">Method</th><th className="text-left p-2">Reference</th><th className="text-left p-2">Bank</th><th className="text-right p-2">Amount</th></tr></thead>
                    <tbody>
                      {payments.map(p => (
                        <tr key={p.id} className="border-t">
                          <td className="p-2">{fmtDate(p.payment_date)}</td>
                          <td className="p-2 uppercase">{p.method}</td>
                          <td className="p-2 font-mono">{p.reference_number || '—'}</td>
                          <td className="p-2">{p.bank || '—'}</td>
                          <td className="p-2 text-right font-mono text-emerald-700">{fmtMoney(p.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Signed-link Activity */}
              {linkActivity && (linkActivity.total_links > 0 || linkActivity.total_views > 0) && (
                <details className="mt-2" open data-testid="link-activity-section">
                  <summary className="text-xs font-semibold text-slate-500 cursor-pointer flex items-center gap-1">
                    <LinkIcon className="h-3 w-3" /> Payment-link activity
                    <span className="ml-1 text-[10px] text-slate-400">
                      · {linkActivity.total_links} link{linkActivity.total_links === 1 ? '' : 's'} · {linkActivity.total_views} view{linkActivity.total_views === 1 ? '' : 's'}
                    </span>
                    {linkActivity.last_viewed_at && (
                      <span className="ml-auto text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5" data-testid="link-activity-last-viewed">
                        Last viewed {new Date(linkActivity.last_viewed_at).toLocaleString()}
                      </span>
                    )}
                  </summary>
                  <div className="mt-2 border rounded divide-y">
                    {(linkActivity.events || []).map((e, i) => (
                      <div key={i} className="p-2 flex items-start gap-2 text-xs" data-testid={`link-activity-row-${i}`}>
                        <ChevronRight className={`h-3 w-3 mt-0.5 ${e.kind === 'viewed' ? 'text-emerald-600' : 'text-blue-600'}`} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono font-semibold uppercase text-[10px]">{e.kind}</span>
                            {e.kind === 'created' && e.actor_email && <span className="text-slate-500">by {e.actor_email}</span>}
                            {e.kind === 'viewed' && (
                              <span className="text-slate-500 truncate max-w-[220px]" title={e.user_agent}>{e.user_agent || '—'}</span>
                            )}
                            {e.ip_prefix && <span className="text-[10px] font-mono bg-slate-100 px-1 rounded">{e.ip_prefix}</span>}
                            <span className="text-[10px] font-mono bg-slate-50 border px-1 rounded text-slate-400">tok:{e.token_ref}</span>
                            <span className="text-slate-400 ml-auto">{new Date(e.at).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {/* Audit */}
              {audit.length > 0 && (
                <details className="mt-2">
                  <summary className="text-xs font-semibold text-slate-500 cursor-pointer flex items-center gap-1"><ScrollText className="h-3 w-3" /> Audit trail ({audit.length})</summary>
                  <div className="mt-2 border rounded divide-y">
                    {audit.map((e, i) => (
                      <div key={i} className="p-2 flex items-start gap-2 text-xs">
                        <ChevronRight className="h-3 w-3 text-slate-400 mt-0.5" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono font-semibold">{e.event}</span>
                            <span className="text-slate-500">by {e.actor}</span>
                            {e.ip && <span className="text-[10px] font-mono bg-slate-100 px-1 rounded">{e.ip}</span>}
                            <span className="text-slate-400 ml-auto">{new Date(e.at).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Issue dialog */}
      {issueOpen && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setIssueOpen(false)}>
          <Card className="w-full max-w-md bg-white" onClick={(e) => e.stopPropagation()} data-testid="issue-dialog">
            <CardHeader><CardTitle className="text-base">Send invoice by email</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label>Recipient email</Label>
                <Input value={issueTo} onChange={(e) => setIssueTo(e.target.value)} data-testid="issue-recipient" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setIssueOpen(false)}>Cancel</Button>
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={doIssue} data-testid="issue-submit"><Send className="h-4 w-4 mr-1" /> Send</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Notify USKE dialog */}
      {notifyOpen && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setNotifyOpen(false)}>
          <Card className="w-full max-w-md bg-white" onClick={(e) => e.stopPropagation()} data-testid="notify-payment-dialog">
            <CardHeader>
              <CardTitle className="text-base">Notify USKE of Payment</CardTitle>
              <p className="text-xs text-slate-500">USKE Finance will verify funds and record the payment. Balance due {fmtMoney(detail?.balance_due)}</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={(e) => { e.preventDefault(); submitNotifyPayment(); }} className="space-y-3">
                <div>
                  <Label>Amount</Label>
                  <Input type="number" step="0.01" min="0.01" value={notifyForm.amount} onChange={(e) => setNotifyForm({ ...notifyForm, amount: e.target.value })} data-testid="notify-amount" />
                </div>
                <div>
                  <Label>Method</Label>
                  <Select value={notifyForm.method} onValueChange={(v) => setNotifyForm({ ...notifyForm, method: v })}>
                    <SelectTrigger data-testid="notify-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ach">ACH</SelectItem>
                      <SelectItem value="eft">EFT</SelectItem>
                      <SelectItem value="wire">Wire</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Reference number</Label>
                  <Input value={notifyForm.reference_number} onChange={(e) => setNotifyForm({ ...notifyForm, reference_number: e.target.value })} placeholder="ACH / wire trace #" data-testid="notify-ref" />
                </div>
                <div>
                  <Label>Payment date</Label>
                  <Input type="date" value={notifyForm.payment_date} onChange={(e) => setNotifyForm({ ...notifyForm, payment_date: e.target.value })} data-testid="notify-date" />
                </div>
                <div>
                  <Label>Bank</Label>
                  <Input value={notifyForm.bank} onChange={(e) => setNotifyForm({ ...notifyForm, bank: e.target.value })} placeholder="Originating bank" data-testid="notify-bank" />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea rows={2} value={notifyForm.notes} onChange={(e) => setNotifyForm({ ...notifyForm, notes: e.target.value })} data-testid="notify-notes" />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setNotifyOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={notifySubmitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="notify-submit">
                    {notifySubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Notify USKE
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Payment dialog */}
      {payOpen && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setPayOpen(false)}>
          <Card className="w-full max-w-md bg-white" onClick={(e) => e.stopPropagation()} data-testid="record-payment-dialog">
            <CardHeader>
              <CardTitle className="text-base">Record Direct Deposit payment</CardTitle>
              <p className="text-xs text-slate-500">Only Direct Deposit (ACH / EFT) is accepted. Balance due {fmtMoney(detail?.balance_due)}</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={submitPayment} className="space-y-3">
                {payPrefillMeta && (
                  <div className="text-[11px] bg-blue-50 border border-blue-200 rounded p-2 text-blue-900" data-testid="pay-prefill-banner">
                    Prefilled from Agency notification by <b>{payPrefillMeta.notified_by || '—'}</b>
                    {payPrefillMeta.notified_at ? ` · ${new Date(payPrefillMeta.notified_at).toLocaleString()}` : ''}
                  </div>
                )}
                <div>
                  <Label>Amount *</Label>
                  <Input type="number" step="0.01" min="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} required data-testid="pay-amount" />
                </div>
                <div>
                  <Label>Direct Deposit type *</Label>
                  <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                    <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAYMENT_METHODS.map(m => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Payment date</Label>
                  <Input type="date" value={payForm.payment_date || ''} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} data-testid="pay-date" />
                </div>
                <div>
                  <Label>Reference number *</Label>
                  <Input value={payForm.reference_number} onChange={(e) => setPayForm({ ...payForm, reference_number: e.target.value })} placeholder="ACH trace # or EFT transaction ID" required data-testid="pay-ref" />
                </div>
                <div>
                  <Label>Bank *</Label>
                  <Input value={payForm.bank} onChange={(e) => setPayForm({ ...payForm, bank: e.target.value })} placeholder="Originating bank" required data-testid="pay-bank" />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea rows={2} value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} data-testid="pay-notes" />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={paySubmitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="pay-submit">
                    {paySubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Receipt className="h-4 w-4 mr-1" />} Record
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Credit note dialog */}
      {crOpen && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setCrOpen(false)}>
          <Card className="w-full max-w-md bg-white" onClick={(e) => e.stopPropagation()} data-testid="credit-note-dialog">
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileMinus className="h-4 w-4 text-amber-700" /> Issue Credit Note</CardTitle><p className="text-xs text-slate-500">Against invoice {detail?.invoice_no} · balance {fmtMoney(detail?.balance_due)}</p></CardHeader>
            <CardContent>
              <form onSubmit={submitCreditNote} className="space-y-3">
                <div>
                  <Label>Reason code *</Label>
                  <Select value={crForm.reason_code} onValueChange={(v) => setCrForm({ ...crForm, reason_code: v })}>
                    <SelectTrigger data-testid="cr-code"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="adjustment">Adjustment</SelectItem>
                      <SelectItem value="cancellation">Cancellation</SelectItem>
                      <SelectItem value="return">Return</SelectItem>
                      <SelectItem value="damaged">Damaged goods</SelectItem>
                      <SelectItem value="duplicate">Duplicate invoice</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Amount *</Label>
                  <Input type="number" step="0.01" min="0.01" value={crForm.total} onChange={(e) => setCrForm({ ...crForm, total: e.target.value })} required data-testid="cr-total" />
                </div>
                <div>
                  <Label>Reason (customer-visible) *</Label>
                  <Textarea rows={3} value={crForm.reason} onChange={(e) => setCrForm({ ...crForm, reason: e.target.value })} required data-testid="cr-reason" />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={crForm.apply_to_invoice} onChange={(e) => setCrForm({ ...crForm, apply_to_invoice: e.target.checked })} data-testid="cr-apply" />
                  Apply to invoice balance
                </label>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setCrOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={crSubmitting} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="cr-submit">
                    {crSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileMinus className="h-4 w-4 mr-1" />} Issue
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default GovInvoices;
